(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root && root.document && !root.FrostixCuratedPagePolish) {
    root.FrostixCuratedPagePolish = api.createController(root);
    root.document.querySelectorAll('curated-selector').forEach(node => root.FrostixCuratedPagePolish.mount(node));
  }
})(typeof window !== 'undefined' ? window : null, function () {
  'use strict';

  function formatPriceText(text) {
    // Only change the separator of a numeric euro token, never its grouping.
    return text.replace(/(\d)\s+(?=€)/g, '$1');
  }

  function scrollState(previous, y, mobile, blocked) {
    y = Math.max(0, y);
    if (!previous || !mobile || blocked || y <= 0) return { y, travel: 0, hidden: false };
    const delta = y - previous.y;
    if (!delta) return previous;
    const travel = Math.sign(delta) === Math.sign(previous.travel) ? previous.travel + delta : delta;
    const hidden = travel >= 12 ? true : travel <= -8 ? false : previous.hidden;
    return { y, travel, hidden };
  }

  function createController(root) {
    const document = root.document;
    const owners = new Set();
    const hiddenClass = 'curated-page-header-hidden';
    const unsafe = 'script, style, noscript, template, textarea, input, select, code, pre, svg, [contenteditable="true"]';
    const overlays = 'cart-drawer.active, cart-drawer[open], quick-add-modal[open], dialog[open], modal-dialog[open], .popup.active, .popup.open, .quick-view.active';
    let controller, mutationObserver, resizeObserver, frame = 0, state, mountedPath;
    let header, main, collections, pointer = false, hovering = false;
    const pending = new Set();
    const walkers = [];

    function syncHeight() {
      if (header) document.documentElement.style.setProperty('--header-height', header.offsetHeight + 'px');
    }

    function measure() {
      if (!document.body.classList.contains(hiddenClass)) {
        if (header) document.body.style.setProperty('--curated-page-header-reserve-height', header.offsetHeight + 'px');
        if (main) document.body.style.setProperty('--curated-header-main-height', main.getBoundingClientRect().height + 'px');
        if (collections) document.body.style.setProperty('--curated-header-collections-height', collections.getBoundingClientRect().height + 'px');
      }
      syncHeight();
    }

    function observeHeight() {
      const transitioning = [main, collections].some(node => node && node.getAnimations &&
        node.getAnimations().some(animation => animation.playState === 'running'));
      if (!document.body.classList.contains(hiddenClass) && !transitioning) measure();
      else syncHeight();
    }

    function blocked() {
      return pointer || hovering || Boolean(header && header.contains(document.activeElement)) ||
        Boolean(document.querySelector(overlays)) || Boolean(header && header.querySelector('details[open], [aria-controls][aria-expanded="true"]'));
    }

    function update() {
      if (!controller) return;
      if (mountedPath !== root.location.pathname) {
        for (const owner of owners) unmount(owner);
        return;
      }
      const mobile = root.matchMedia('(max-width: 749px)').matches;
      state = scrollState(state, root.scrollY || 0, mobile, blocked());
      document.body.classList.toggle(hiddenClass, state.hidden);
      syncHeight();
    }

    function enqueue(node) {
      if (!controller) return;
      if (node && node.isConnected) pending.add(node);
      if (!frame) frame = root.requestAnimationFrame(flush);
    }

    function allowed(node) {
      const parent = node.nodeType === 1 ? node : node.parentElement;
      return parent && !parent.closest(unsafe + ', [hidden], [aria-hidden="true"]');
    }

    function format(node) {
      if (node.nodeType !== 3 || !node.isConnected || !allowed(node)) return;
      const text = formatPriceText(node.nodeValue);
      if (text !== node.nodeValue) node.nodeValue = text;
    }

    function flush() {
      frame = 0;
      if (!controller) return;
      // Walk only changed subtrees, at most 200 accepted text nodes per frame.
      for (const node of pending) {
        if (!node.isConnected || !allowed(node)) continue;
        if (node.nodeType === 3) {
          let current = node;
          walkers.push({ nextNode() { const value = current; current = null; return value; } });
        } else walkers.push(document.createTreeWalker(node, 5, {
          acceptNode(candidate) {
            if (!allowed(candidate)) return 2;
            return candidate.nodeType === 3 ? 1 : 3;
          }
        }));
      }
      pending.clear();
      let budget = 200;
      while (walkers.length && budget > 0) {
        const node = walkers[0].nextNode();
        if (!node) { walkers.shift(); continue; }
        format(node); budget--;
      }
      update();
      if (walkers.length) frame = root.requestAnimationFrame(flush);
    }

    function resize() {
      // Drop the scoped transition as well as the hidden state for a synchronous,
      // natural measurement; intermediate animation heights must never become reserves.
      document.body.classList.remove(hiddenClass, 'curated-page-polish');
      state = scrollState(null, root.scrollY || 0, false, false);
      measure();
      document.body.classList.add('curated-page-polish');
      enqueue(document.body);
    }

    function start() {
      for (const owner of owners) if (!owner.isConnected) unmount(owner);
      if (controller || !owners.size) return;
      controller = new root.AbortController();
      const options = { signal: controller.signal };
      mountedPath = root.location.pathname;
      header = document.querySelector('.section-header');
      main = document.querySelector('.header-wrapper > header.header');
      collections = document.querySelector('.header-wrapper > .header__collections-slider');
      document.body.classList.add('curated-page-polish');
      state = scrollState(null, root.scrollY || 0, false, false);
      measure();
      root.addEventListener('scroll', update, { ...options, passive: true });
      root.addEventListener('resize', resize, options);
      document.addEventListener('focusin', update, options);
      document.addEventListener('focusout', () => enqueue(document.body), options);
      document.addEventListener('pointerdown', () => { pointer = true; update(); }, options);
      document.addEventListener('pointerup', () => { pointer = false; update(); }, options);
      document.addEventListener('pointercancel', () => { pointer = false; update(); }, options);
      if (header) {
        header.addEventListener('pointerenter', () => { hovering = true; update(); }, options);
        header.addEventListener('pointerleave', () => { hovering = false; update(); }, options);
      }
      document.addEventListener('shopify:section:unload', event => {
        for (const owner of owners) if (event.target === owner || event.target.contains(owner)) unmount(owner);
      }, options);
      mutationObserver = new root.MutationObserver(records => {
        for (const record of records) {
          if (record.type === 'childList') {
            record.addedNodes.forEach(enqueue);
            for (const owner of owners) if (!owner.isConnected) unmount(owner);
          } else if (record.type === 'characterData') enqueue(record.target);
          else if (record.attributeName !== 'class' || record.target !== document.body) enqueue(record.target);
        }
        update();
      });
      mutationObserver.observe(document.body, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['open', 'class', 'hidden', 'aria-hidden', 'aria-expanded'] });
      if (root.ResizeObserver && header) {
        // Keep genuine expanded-layout changes, but never capture animation frames.
        resizeObserver = new root.ResizeObserver(observeHeight);
        resizeObserver.observe(header);
      }
      enqueue(document.body);
    }

    function stop() {
      if (!controller) return;
      controller.abort(); controller = null;
      mutationObserver.disconnect();
      if (resizeObserver) resizeObserver.disconnect();
      if (frame) root.cancelAnimationFrame(frame);
      frame = 0; pending.clear(); walkers.length = 0;
      pointer = hovering = false;
      document.body.classList.remove(hiddenClass, 'curated-page-polish');
      document.body.style.removeProperty('--curated-page-header-reserve-height');
      document.body.style.removeProperty('--curated-header-main-height');
      document.body.style.removeProperty('--curated-header-collections-height');
      // Return the shared variable to the natural header; never retain a collapsed height.
      syncHeight();
    }

    function mount(owner) {
      if (!owner.isConnected || owners.has(owner)) return;
      owners.add(owner);
      if (owners.size === 1) {
        root.addEventListener('pagehide', stop);
        root.addEventListener('pageshow', start);
      }
      start();
    }

    function unmount(owner) {
      owners.delete(owner);
      if (owners.size) return;
      stop();
      root.removeEventListener('pagehide', stop);
      root.removeEventListener('pageshow', start);
    }

    return { mount, unmount };
  }

  return { formatPriceText, scrollState, createController };
});
